import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Box, FormControl, InputLabel } from '@mui/material';
import { DateTime } from 'luxon';
import { useAnimatedScroll } from '@core/hooks/useAnimatedScroll';

interface TimeSelectorProps {
    value: string; // 'HH:mm'
    onChange: (time: string) => void;
    label?: string;
    placeholder?: string;
    disabled?: boolean;
    minutesStep?: number;
    maxTime?: DateTime;
    minTime?: DateTime;
    className?: string;
    sx?: any;
}

const ClockIcon = () => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.5" />
        <path
            d="M8 4.5V8L10.5 10"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

const pad = (n: number) => String(n).padStart(2, '0');

const parseTime = (value: string): { hour: number; minute: number } | null => {
    if (!value) return null;
    const parts = value.split(':');
    if (parts.length < 2) return null;
    const hour = parseInt(parts[0], 10);
    const minute = parseInt(parts[1], 10);
    if (isNaN(hour) || isNaN(minute)) return null;
    return { hour, minute };
};

const TimeSelector: React.FC<TimeSelectorProps> = ({
    value,
    onChange,
    label,
    placeholder = 'select',
    disabled = false,
    minutesStep = 5,
    maxTime,
    minTime,
    className = '',
    sx = {}
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [isClosing, setIsClosing] = useState(false);
    const [isTyping, setIsTyping] = useState(false);
    const [inputText, setInputText] = useState('');
    const [isMobile, setIsMobile] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const hourListRef = useRef<HTMLDivElement>(null);
    const minuteListRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    const parsed = parseTime(value);
    const displayValue = parsed ? `${pad(parsed.hour)}:${pad(parsed.minute)}` : null;

    const hours = Array.from({ length: 24 }, (_, i) => i);
    const minutes = Array.from({ length: Math.floor(60 / minutesStep) }, (_, i) => i * minutesStep);

    const hourScroll = useAnimatedScroll({
        msPerItem: 100,
        minDuration: 700
    });

    const minuteScroll = useAnimatedScroll({
        msPerItem: 50,
        minDuration: 700
    });

    // Detect mobile
    useEffect(() => {
        const check = () => setIsMobile(window.innerWidth <= 480 || 'ontouchstart' in window);
        check();
        window.addEventListener('resize', check);
        return () => window.removeEventListener('resize', check);
    }, []);

    const isHourDisabled = useCallback(
        (h: number): boolean => {
            if (minTime && h < minTime.hour) return true;
            if (maxTime && h > maxTime.hour) return true;
            return false;
        },
        [minTime, maxTime]
    );

    const isMinuteDisabled = useCallback(
        (m: number): boolean => {
            if (!parsed) return false;
            if (minTime && parsed.hour === minTime.hour && m < minTime.minute) return true;
            if (maxTime && parsed.hour === maxTime.hour && m > maxTime.minute) return true;
            return false;
        },
        [parsed, minTime, maxTime]
    );

    // Scroll to selected item when dropdown opens.
    useEffect(() => {
        if (!isOpen || !parsed) return;

        let rafId: number;

        const scrollToSelected = () => {
            hourScroll.scrollToSelectorFast(hourListRef.current, '.time-option--selected');

            minuteScroll.scrollToSelector(minuteListRef.current, '.time-option--selected');
        };

        rafId = requestAnimationFrame(() => {
            requestAnimationFrame(scrollToSelected);
        });

        return () => cancelAnimationFrame(rafId);
    }, [isOpen]);

    const handleClose = useCallback(() => {
        if (isOpen) setIsClosing(true);
        setIsTyping(false);
        setInputText('');
    }, [isOpen]);

    const handleMenuAnimationEnd = () => {
        if (isClosing) {
            setIsClosing(false);
            setIsOpen(false);
        }
    };

    useEffect(() => {
        if (!isOpen && !isClosing && !isTyping) return;
        const handleClickOutside = (e: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
                handleClose();
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isOpen, isClosing, isTyping, handleClose]);

    const handleBoxClick = () => {
        if (disabled) return;
        if (isMobile) {
            if (isOpen || isClosing) {
                handleClose();
            } else {
                setIsOpen(true);
                setIsTyping(false);
            }
        } else {
            setIsTyping(true);
            if (isOpen) handleClose();
            setInputText(displayValue || '');
            setTimeout(() => inputRef.current?.focus(), 50);
        }
    };

    const handleClockIconClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (disabled) return;
        setIsTyping(false);
        setInputText('');
        if (isOpen || isClosing) {
            handleClose();
        } else {
            setIsOpen(true);
        }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        let raw = e.target.value.replace(/[^0-9:]/g, '');
        if (raw.length === 2 && !raw.includes(':') && inputText.length === 1) {
            raw = raw + ':';
        }
        if (raw.length > 5) return;
        setInputText(raw);

        const match = raw.match(/^(\d{1,2}):(\d{2})$/);
        if (match) {
            const h = parseInt(match[1], 10);
            const m = parseInt(match[2], 10);
            if (h >= 0 && h <= 23 && m >= 0 && m <= 59) {
                onChange(`${pad(h)}:${pad(m)}`);
            }
        }
    };

    const handleInputBlur = () => {
        setIsTyping(false);
        setInputText('');
    };

    const handleInputKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === 'Escape') {
            setIsTyping(false);
            setInputText('');
        }
    };

    const handleSelectHour = (h: number) => {
        if (isHourDisabled(h)) return;
        const currentMinute = parsed?.minute ?? 0;
        let newMinute = currentMinute;
        if (minTime && h === minTime.hour && currentMinute < minTime.minute) {
            newMinute = minutes.find((m) => m >= minTime.minute) ?? minTime.minute;
        }
        if (maxTime && h === maxTime.hour && currentMinute > maxTime.minute) {
            newMinute = [...minutes].reverse().find((m) => m <= maxTime.minute) ?? maxTime.minute;
        }
        onChange(`${pad(h)}:${pad(newMinute)}`);
    };

    const handleSelectMinute = (m: number) => {
        if (isMinuteDisabled(m)) return;
        const currentHour = parsed?.hour ?? 0;
        onChange(`${pad(currentHour)}:${pad(m)}`);
    };

    const outerWrapperStyle = disabled ? { cursor: 'not-allowed' } : {};
    const innerWrapperStyle = disabled ? { pointerEvents: 'none' as const } : {};
    const isMenuVisible = isOpen || isClosing;

    return (
        <Box
            className={`selector-wrapper time-selector-wrapper ${className}`}
            ref={containerRef}
            sx={{ width: 120, ...sx }}
            style={outerWrapperStyle}
        >
            <FormControl
                fullWidth
                disabled={disabled}
                className={`selector-container ${isOpen || isTyping ? 'open' : ''} ${disabled ? 'disabled' : ''}`}
            >
                {label && (
                    <InputLabel shrink className="selector-label">
                        {label}
                    </InputLabel>
                )}

                <Box className="selector-input-wrapper" style={innerWrapperStyle}>
                    <Box className="selector-input-box" style={{ padding: 0, overflow: 'hidden' }}>
                        {/* Left area: text / typing */}
                        <Box
                            onClick={handleBoxClick}
                            style={{
                                flex: 1,
                                display: 'flex',
                                alignItems: 'center',
                                padding: '10px 0 10px 15px',
                                cursor: isMobile ? 'pointer' : 'text',
                                minWidth: 0
                            }}
                        >
                            {isTyping ? (
                                <input
                                    ref={inputRef}
                                    value={inputText}
                                    onChange={handleInputChange}
                                    onBlur={handleInputBlur}
                                    onKeyDown={handleInputKeyDown}
                                    placeholder="HH:mm"
                                    style={{
                                        border: 'none',
                                        outline: 'none',
                                        background: 'transparent',
                                        fontSize: '14px',
                                        fontWeight: 700,
                                        width: '60px',
                                        fontFamily: 'inherit',
                                        color: 'inherit',
                                        cursor: 'text'
                                    }}
                                />
                            ) : displayValue ? (
                                <span className="selector-selected-value">{displayValue}</span>
                            ) : (
                                <span className="selector-placeholder">{placeholder}</span>
                            )}
                        </Box>

                        {/* Right area: clock icon */}
                        <Box
                            onClick={handleClockIconClick}
                            className="selector-icon time-selector-clock-zone"
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                alignSelf: 'stretch',
                                padding: '0 10px',
                                cursor: 'pointer',
                                flexShrink: 0
                            }}
                        >
                            <ClockIcon />
                        </Box>
                    </Box>

                    {isMenuVisible && (
                        <Box
                            className={`time-selector-menu selector-menu${isClosing ? ' closing' : ''}`}
                            onAnimationEnd={handleMenuAnimationEnd}
                        >
                            <Box className="time-selector-columns">
                                {/* Hours */}
                                <Box className="time-selector-column" ref={hourListRef}>
                                    {hours.map((h) => {
                                        const isSelected = parsed?.hour === h;
                                        const isDisabled = isHourDisabled(h);
                                        return (
                                            <Box
                                                key={h}
                                                className={`time-option ${isSelected ? 'time-option--selected' : ''} ${isDisabled ? 'time-option--disabled' : ''}`}
                                                onClick={() => !isDisabled && handleSelectHour(h)}
                                            >
                                                {pad(h)}
                                            </Box>
                                        );
                                    })}
                                </Box>

                                <Box className="time-selector-divider" />

                                {/* Minutes */}
                                <Box className="time-selector-column" ref={minuteListRef}>
                                    {minutes.map((m) => {
                                        const isSelected = parsed?.minute === m;
                                        const isDisabled = isMinuteDisabled(m);
                                        return (
                                            <Box
                                                key={m}
                                                className={`time-option ${isSelected ? 'time-option--selected' : ''} ${isDisabled ? 'time-option--disabled' : ''}`}
                                                onClick={() => !isDisabled && handleSelectMinute(m)}
                                            >
                                                {pad(m)}
                                            </Box>
                                        );
                                    })}
                                </Box>
                            </Box>
                        </Box>
                    )}
                </Box>
            </FormControl>
        </Box>
    );
};

export default TimeSelector;
